/**
 * TransformChatFlow.tsx — Transform Slide as a conversation, per the mockup.
 *
 * The mockup runs this feature entirely in the chat column: the "Transform
 * slide" chip echoes the user's choice, AiVA asks which mode to use, progress
 * streams into the thread, and the result arrives with follow-up pills. This
 * component is that flow.
 *
 * It renders BELOW the message list and above the chip dock — where the next
 * message would appear anyway — but keeps its state locally instead of pushing
 * into `useChat`'s `messages`. That thread is owned by the chat backend: it is
 * persisted, rehydrated and reconciled against server state, so client-only
 * turns carrying live-updating progress would either be dropped on reload or
 * confuse reconciliation, for every host. Locally held, the flow looks the same
 * and costs the chat thread nothing.
 *
 * Mounting nothing when the flow is closed keeps it inert until the user asks
 * for it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { AivaBubble, UserBubble, type AivaPill } from "./AivaBubble";
import { StageList } from "./StageList";
import { TemplatePicker } from "./TemplatePicker";
import { useSlideTransform } from "../hooks/useSlideTransform";
import { MAX_TEMPLATE_PICKS, useTemplateTransform } from "../hooks/useTemplateTransform";
import { useStagedProgress } from "../hooks/useStagedProgress";
import {
  canUseTemplatePickerDialog,
  openTemplatePickerDialog,
} from "../hooks/openTemplatePickerDialog";
import { hasLowerTierPick as picksSpanLowerTier, resolveFitMode } from "../services/templateFitPolicy";
import {
  TEMPLATE_APPLY_FIT_STAGE_INDEX,
  TEMPLATE_APPLY_PROGRESS_TITLE,
  TEMPLATE_APPLY_STAGES,
  TEMPLATE_FIT_STAGE_LABEL,
  TEMPLATE_MATCH_PROGRESS_TITLE,
  TEMPLATE_MATCH_STAGES,
  TEMPLATE_MATCH_STAGE_INTERVAL_MS,
  TRANSFORM_PROGRESS_TITLE,
  TRANSFORM_STAGES,
  TRANSFORM_STAGE_INTERVAL_MS,
} from "../services/transformStages";
import { reportSlideTransformFailure } from "../services/slideTransformTelemetry";
import type { TemplateFitMode } from "../services/slideTransformTemplateApi";
import styles from "./TransformChatFlow.module.css";

const MILLISECONDS_PER_SECOND = 1000;

/** Which branch the user chose. `null` while AiVA's question is unanswered. */
type Mode = "ai" | "template" | null;

/**
 * Where the layout choice is being made.
 *
 * Per the mockup the picker is an external large modal, so on a host with the
 * Dialog API it opens as its own window and the pane just narrates. `"inline"`
 * is the fallback for hosts without it (and for the web preview and tests),
 * where the picker still renders in the pane rather than being unreachable.
 */
type PickerSurface = "idle" | "window-open" | "window-dismissed" | "inline";

export interface TransformChatFlowProps {
  /** Closes the flow and clears it from the column. */
  readonly onClose: () => void;
  /**
   * `"chat"` (default) docks the flow above the composer in the chat column;
   * `"panel"` lets it fill the routed Transform Slide panel, where it is the
   * whole surface.
   */
  readonly variant?: "chat" | "panel";
}

/**
 * The AI-design result line: how many slides landed, and how many didn't.
 *
 * A function rather than an inline expression because the sentence has three
 * independent variables (none added / singular / plural, and a shortfall clause
 * that only appears when something failed) — nested in JSX that reads as a
 * thicket of ternaries.
 */
function aiResultSummary(
  slides: readonly { readonly status: string }[],
  elapsed: string,
): string {
  const added = slides.filter((slide) => slide.status === "done").length;
  if (added === 0) return "No slides could be redesigned.";

  const noun = added === 1 ? "slide" : "slides";
  const failed = slides.filter((slide) => slide.status === "failed").length;
  const shortfall = failed > 0 ? ` ${failed} couldn't be redesigned.` : "";
  return `Redesigned ${added} ${noun} — inserted after the original${elapsed}.${shortfall}`;
}

/** The template result line — one variant, or several inserted in pick order. */
function templateResultSummary(
  outcomes: readonly { readonly status: string }[],
  elapsed: string,
): string {
  const done = outcomes.filter((outcome) => outcome.status === "done").length;
  return done > 1
    ? `All ${done} variations inserted — review them in your deck${elapsed}.`
    : `Template applied — inserted after the original${elapsed}.`;
}

/** Per-template or per-slide outcome rows shown under the result bubble. */
function SummaryRows({
  rows,
}: Readonly<{ rows: readonly { key: string; name: string; ok: boolean; detail?: string }[] }>) {
  if (rows.length === 0) return null;
  return (
    <ul className={styles.summaryList}>
      {rows.map((row) => (
        <li key={row.key} className={styles.summaryRow}>
          <span className={styles.rowName}>{row.name}</span>
          <span className={row.ok ? styles.rowDone : styles.rowFailed}>
            {row.ok ? "Added" : "Failed"}
          </span>
          {row.detail ? <span className={styles.rowDetail}>{row.detail}</span> : null}
        </li>
      ))}
    </ul>
  );
}

type AiState = ReturnType<typeof useSlideTransform>["state"];
type TemplateState = ReturnType<typeof useTemplateTransform>["state"];

/**
 * The AI-design half of the conversation.
 *
 * Split out of `TransformChatFlow` so each branch tests one phase value instead
 * of repeating `mode === "ai" && …`, and so the parent stays readable as the
 * shape of the conversation rather than a wall of conditionals.
 */
function AiBranch({
  state,
  busy,
  stage,
  elapsed,
  closePill,
  onCancel,
  onStartOver,
}: Readonly<{
  state: AiState;
  busy: boolean;
  stage: number;
  elapsed: string;
  closePill: AivaPill;
  onCancel: () => void;
  onStartOver: () => void;
}>) {
  if (busy) {
    return (
      <AivaBubble meta="working" pills={[{ label: "Cancel", onClick: onCancel }]}>
        <StageList
          stages={TRANSFORM_STAGES}
          activeIndex={stage}
          title={TRANSFORM_PROGRESS_TITLE}
          bare
        />
      </AivaBubble>
    );
  }

  if (state.phase !== "done") return null;

  return (
    <AivaBubble pills={[{ label: "Transform again", onClick: onStartOver }, closePill]}>
      <p>{aiResultSummary(state.slides, elapsed)}</p>
      <SummaryRows
        rows={state.slides.map((slide) => ({
          key: String(slide.index),
          name: `Slide ${slide.index + 1}`,
          ok: slide.status === "done",
          ...(slide.status === "failed" && slide.detail ? { detail: slide.detail } : {}),
        }))}
      />
    </AivaBubble>
  );
}

/** The three things that can be on screen while the user is choosing layouts. */
function PickingBubble({
  surface,
  candidateCount,
  picks,
  effectiveFitMode,
  fitModeLocked,
  candidates,
  closePill,
  onSwitchToAi,
  onReopenWindow,
  onTogglePick,
  onFitModeChange,
  onApplyPicks,
}: Readonly<{
  surface: PickerSurface;
  candidateCount: number;
  picks: readonly string[];
  effectiveFitMode: TemplateFitMode;
  fitModeLocked: boolean;
  candidates: TemplateState["candidates"];
  closePill: AivaPill;
  onSwitchToAi: () => void;
  onReopenWindow: () => void;
  onTogglePick: (templateId: string) => void;
  onFitModeChange: (mode: TemplateFitMode) => void;
  onApplyPicks: () => void;
}>) {
  // The mockup puts the picker in an external large modal, so while that window
  // is up the pane narrates rather than duplicating the grid.
  if (surface === "window-open") {
    return (
      <AivaBubble pills={[{ label: "Use AI design instead", onClick: onSwitchToAi }, closePill]}>
        <p>
          I found {candidateCount} matching {candidateCount === 1 ? "layout" : "layouts"} — choose up
          to {MAX_TEMPLATE_PICKS} in the template window.
        </p>
      </AivaBubble>
    );
  }

  // Closed without choosing. The candidates are still here, so offer them again
  // rather than making the user sit through another match.
  if (surface === "window-dismissed") {
    return (
      <AivaBubble
        pills={[
          { label: "Choose templates", onClick: onReopenWindow, primary: true },
          { label: "Use AI design instead", onClick: onSwitchToAi },
          closePill,
        ]}
      >
        <p>
          The template window was closed. Your {candidateCount} matched{" "}
          {candidateCount === 1 ? "layout is" : "layouts are"} still ready when you are.
        </p>
      </AivaBubble>
    );
  }

  if (surface !== "inline") return null;

  // No Dialog API, or the window failed to open.
  return (
    <AivaBubble
      pills={[
        {
          label: picks.length > 1 ? `Apply ${picks.length} templates` : "Apply template",
          onClick: onApplyPicks,
          primary: true,
          disabled: picks.length === 0,
          ...(picks.length === 0 ? { title: "Pick at least one layout" } : {}),
        },
        { label: "Use AI design instead", onClick: onSwitchToAi },
        closePill,
      ]}
    >
      <TemplatePicker
        candidates={candidates}
        selected={picks}
        onToggle={onTogglePick}
        fitMode={effectiveFitMode}
        onFitModeChange={onFitModeChange}
        fitModeLocked={fitModeLocked}
      />
    </AivaBubble>
  );
}

/** The template half of the conversation: match → pick → apply → done. */
function TemplateBranch({
  state,
  surface,
  matchStage,
  picks,
  effectiveFitMode,
  fitModeLocked,
  elapsed,
  closePill,
  onCancel,
  onStartOver,
  ...picking
}: Readonly<{
  state: TemplateState;
  surface: PickerSurface;
  matchStage: number;
  picks: readonly string[];
  effectiveFitMode: TemplateFitMode;
  fitModeLocked: boolean;
  elapsed: string;
  closePill: AivaPill;
  onCancel: () => void;
  onStartOver: () => void;
  onSwitchToAi: () => void;
  onReopenWindow: () => void;
  onTogglePick: (templateId: string) => void;
  onFitModeChange: (mode: TemplateFitMode) => void;
  onApplyPicks: () => void;
}>) {
  if (state.phase === "matching") {
    return (
      <AivaBubble meta="working" pills={[{ label: "Cancel", onClick: onCancel }]}>
        <StageList
          stages={TEMPLATE_MATCH_STAGES}
          activeIndex={matchStage}
          title={TEMPLATE_MATCH_PROGRESS_TITLE}
          bare
        />
      </AivaBubble>
    );
  }

  if (state.phase === "picking") {
    return (
      <PickingBubble
        surface={surface}
        candidateCount={state.candidates.length}
        candidates={state.candidates}
        picks={picks}
        effectiveFitMode={effectiveFitMode}
        fitModeLocked={fitModeLocked}
        closePill={closePill}
        {...picking}
      />
    );
  }

  if (state.phase === "applying") {
    return (
      <AivaBubble meta="working" pills={[{ label: "Cancel", onClick: onCancel }]}>
        <StageList
          stages={TEMPLATE_APPLY_STAGES.map((stage, index) =>
            index === TEMPLATE_APPLY_FIT_STAGE_INDEX
              ? TEMPLATE_FIT_STAGE_LABEL[effectiveFitMode]
              : stage,
          )}
          activeIndex={state.applyStage}
          title={TEMPLATE_APPLY_PROGRESS_TITLE}
          bare
        />
      </AivaBubble>
    );
  }

  if (state.phase !== "done") return null;

  return (
    <AivaBubble pills={[{ label: "Transform again", onClick: onStartOver }, closePill]}>
      <p>{templateResultSummary(state.outcomes, elapsed)}</p>
      <SummaryRows
        rows={state.outcomes.map((outcome) => ({
          key: outcome.templateId,
          name: outcome.templateId.replace(/^.*\//, "").replace(/\.pptx.*$/i, ""),
          ok: outcome.status === "done",
          ...(outcome.status === "failed" && outcome.detail ? { detail: outcome.detail } : {}),
        }))}
      />
    </AivaBubble>
  );
}

export function TransformChatFlow({ onClose, variant = "chat" }: Readonly<TransformChatFlowProps>) {
  const [mode, setMode] = useState<Mode>(null);
  const [picks, setPicks] = useState<readonly string[]>([]);
  const [fitMode, setFitMode] = useState<TemplateFitMode>("preserve");
  const [pickerSurface, setPickerSurface] = useState<PickerSurface>("idle");
  const [elapsedSeconds, setElapsedSeconds] = useState<number | null>(null);

  const ai = useSlideTransform();
  const template = useTemplateTransform();

  const endRef = useRef<HTMLDivElement | null>(null);

  const aiBusy = ai.state.phase === "reading" || ai.state.phase === "transforming";
  const templateBusy = template.state.phase === "matching" || template.state.phase === "applying";
  const busy = aiBusy || templateBusy;

  const aiStage = useStagedProgress(TRANSFORM_STAGES.length, TRANSFORM_STAGE_INTERVAL_MS, aiBusy);
  const matchStage = useStagedProgress(
    TEMPLATE_MATCH_STAGES.length,
    TEMPLATE_MATCH_STAGE_INTERVAL_MS,
    template.state.phase === "matching",
  );

  // Wall-clock duration, reported with the result as the mockup does.
  const startedAtRef = useRef<number | null>(null);
  useEffect(() => {
    if (busy) {
      startedAtRef.current ??= Date.now();
      return;
    }
    if (startedAtRef.current === null) return;
    const seconds = Math.round((Date.now() - startedAtRef.current) / MILLISECONDS_PER_SECOND);
    startedAtRef.current = null;
    setElapsedSeconds(seconds);
  }, [busy]);

  // Keep the newest bubble in view, the way a chat thread does. Guarded because
  // scrollIntoView is not implemented everywhere the pane renders (JSDOM, and
  // older embedded web views) — an exception here would unmount the whole flow
  // over a cosmetic scroll.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ block: "end" });
  }, [ai.state.phase, template.state.phase, mode]);

  /**
   * A pick from below the best-matches tier was not auto-matched to this slide,
   * so its content is always reshaped — the mockup's "browse picks force AI
   * rewrite" rule. Locking it here keeps the control honest about what will
   * actually happen. The rule itself lives in `templateFitPolicy` so this
   * fallback and the dialog cannot disagree about it.
   */
  const candidates = template.state.candidates;
  const hasLowerTierPick = picksSpanLowerTier(picks, candidates);
  const effectiveFitMode: TemplateFitMode = resolveFitMode(picks, candidates, fitMode);

  const chooseAi = useCallback(() => {
    setMode("ai");
    setElapsedSeconds(null);
    ai.transform().catch(reportSlideTransformFailure("slide-transform-chat-ai"));
  }, [ai]);

  const chooseTemplate = useCallback(() => {
    setMode("template");
    setElapsedSeconds(null);
    setPicks([]);
    setPickerSurface("idle");
    template.match().catch(reportSlideTransformFailure("slide-transform-chat-template"));
  }, [template]);

  /**
   * Run the transform for an explicit set of picks.
   *
   * Takes them as arguments rather than reading state because the dialog
   * returns its own picks: routing them through `setPicks` first would apply
   * whatever the previous render held.
   */
  const runApply = useCallback(
    (chosen: readonly string[], fit: TemplateFitMode) => {
      setElapsedSeconds(null);
      template.apply(chosen, fit).catch(reportSlideTransformFailure("slide-transform-chat-apply"));
    },
    [template],
  );

  const applyPicks = useCallback(
    () => runApply(picks, effectiveFitMode),
    [runApply, picks, effectiveFitMode],
  );

  const switchToAi = useCallback(() => {
    template.reset();
    chooseAi();
  }, [template, chooseAi]);

  const startOver = useCallback(() => {
    ai.reset();
    template.reset();
    setMode(null);
    setPicks([]);
    setFitMode("preserve");
    setPickerSurface("idle");
    setElapsedSeconds(null);
  }, [ai, template]);

  /**
   * Hand the ranked candidates to the picker window as soon as they land.
   *
   * Opens once per match: `pickerSurface` leaves `"idle"` on the first run and
   * only returns there via `startOver`, so a re-render (or the user reopening
   * after a dismiss) can't stack a second window on the same results.
   *
   * A host without the Dialog API, or a window that fails to open, falls back
   * to the in-pane picker rather than stranding the candidates — the user has
   * already waited for the retrieve.
   */
  const openPickerWindow = useCallback(() => {
    if (!canUseTemplatePickerDialog()) {
      setPickerSurface("inline");
      return;
    }
    setPickerSurface("window-open");
    openTemplatePickerDialog(candidates)
      .then((result) => {
        if (result.kind === "apply") {
          setPicks(result.picks);
          setFitMode(result.fitMode);
          setPickerSurface("idle");
          runApply(result.picks, result.fitMode);
          return;
        }
        if (result.kind === "use-ai") {
          setPickerSurface("idle");
          switchToAi();
          return;
        }
        setPickerSurface("window-dismissed");
      })
      .catch((error: unknown) => {
        reportSlideTransformFailure("slide-transform-template-picker-window")(error);
        setPickerSurface("inline");
      });
  }, [candidates, runApply, switchToAi]);

  useEffect(() => {
    if (mode !== "template") return;
    if (template.state.phase !== "picking") return;
    if (pickerSurface !== "idle") return;
    openPickerWindow();
  }, [mode, template.state.phase, pickerSurface, openPickerWindow]);

  const togglePick = useCallback((templateId: string) => {
    setPicks((previous) => {
      if (previous.includes(templateId)) return previous.filter((id) => id !== templateId);
      if (previous.length >= MAX_TEMPLATE_PICKS) return previous;
      return [...previous, templateId];
    });
  }, []);

  const closePill: AivaPill = { label: "Close", onClick: onClose };
  const elapsed = elapsedSeconds !== null ? ` (${elapsedSeconds}s)` : "";

  return (
    <div
      className={`${styles.flow} ${variant === "panel" ? styles.flowPanel : ""}`}
      aria-label="Transform slide"
    >
      <UserBubble>Transform slide</UserBubble>

      {/* ── AiVA's mode question ── */}
      {mode === null ? (
        <AivaBubble
          pills={[
            { label: "AI design", onClick: chooseAi, primary: true },
            { label: "Use templates", onClick: chooseTemplate },
            closePill,
          ]}
        >
          <p>
            How would you like to transform this slide? <strong>AI design</strong> re-architects it
            from scratch. <strong>Use templates</strong> fits your content into a reviewed layout.
          </p>
          <p>Either way the redesign is added after the original, so nothing is lost.</p>
        </AivaBubble>
      ) : null}

      {/* ── AI design ── */}
      {mode === "ai" ? (
        <AiBranch
          state={ai.state}
          busy={aiBusy}
          stage={aiStage}
          elapsed={elapsed}
          closePill={closePill}
          onCancel={ai.cancel}
          onStartOver={startOver}
        />
      ) : null}

      {/* ── Template mode: match → pick → apply → done ── */}
      {mode === "template" ? (
        <TemplateBranch
          state={template.state}
          surface={pickerSurface}
          matchStage={matchStage}
          picks={picks}
          effectiveFitMode={effectiveFitMode}
          fitModeLocked={hasLowerTierPick}
          elapsed={elapsed}
          closePill={closePill}
          onCancel={template.cancel}
          onStartOver={startOver}
          onSwitchToAi={switchToAi}
          onReopenWindow={openPickerWindow}
          onTogglePick={togglePick}
          onFitModeChange={setFitMode}
          onApplyPicks={applyPicks}
        />
      ) : null}

      {/* ── Cancelled / failed, either mode ── */}
      {ai.state.phase === "cancelled" || template.state.phase === "cancelled" ? (
        <AivaBubble pills={[{ label: "Try again", onClick: startOver }, closePill]}>
          <p>Cancelled. Any slides already added were kept.</p>
        </AivaBubble>
      ) : null}

      {ai.state.phase === "error" || template.state.phase === "error" ? (
        <AivaBubble pills={[{ label: "Try again", onClick: startOver }, closePill]}>
          <p className={styles.error} role="alert">
            {ai.state.error ?? template.state.error}
          </p>
        </AivaBubble>
      ) : null}

      <div ref={endRef} />
    </div>
  );
}
